import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import "react-datepicker/dist/react-datepicker.css";
import { FormInputEvent } from '../../types/form';

interface AdminUserFormData {
  fullName: string;
  username: string;
  email: string;
  phoneNumber: string;
  passwordHash: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  subcompanyId: string;
}

export default function AdminUserForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<AdminUserFormData>({
    fullName: "",
    username: "",
    email: "",
    phoneNumber: "",
    passwordHash: "",
    isActive: true,
    isSuperAdmin: false,
    subcompanyId: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchUserData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/admins/${id}`);
      const userData = response.data;
      setFormData(userData);
    } catch (error) {
      setError("Failed to fetch user data");
      console.error("Error fetching user:", error);
    }
  }, [id]);

  useEffect(() => {
    if (isEditMode) {
      fetchUserData();
    }
  }, [isEditMode, fetchUserData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const submitData = formData;

      if (isEditMode) {
        await axiosInstance.put(`/admins/${id}`, submitData);
      } else {
        await axiosInstance.post("/admins", submitData);
      }
      navigate("/admin-user");
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to save user");
      console.error("Error saving user:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: FormInputEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="w-full space-y-6">
      <PageBreadcrumb pageTitle={isEditMode ? "Edit Admin User" : "Add Admin User"} />

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        {error && (
          <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            <InputWithLabel
              label="Full Name"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="Phone Number"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="Subcompany ID"
              name="subcompanyId"
              value={formData.subcompanyId}
              onChange={handleInputChange}
              required
            />

            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span>Is Active</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="isSuperAdmin"
                  checked={formData.isSuperAdmin}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span>Is Super Admin</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/admin-user")}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
